import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import { Reveal, MaskLines } from "../components/Motion";
import { useFetch } from "../lib/useFetch";
import { asList } from "../lib/api";

const CATS = ["All", "Strategy", "Growth", "People", "Finance", "Leadership", "Operations", "Technology", "Macroeconomics"];
const TYPES = [["", "Everything"], ["research", "Peer-reviewed"], ["news", "Analysis"]];

export default function Articles() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState(params.get("q") || "");
  const qs = new URLSearchParams({ ...(type ? { type } : {}), ...(q.trim() ? { q: q.trim() } : {}) }).toString();
  const { data, loading } = useFetch(`/articles${qs ? `?${qs}` : ""}`, []);
  const list = useMemo(() => asList(data).filter((a) => cat === "All" || a.category === cat), [data, cat]);

  return (
    <div className="px-5 lg:px-12 pt-14" data-testid="articles-page">
      <p className="eyebrow mb-6">Library</p>
      <h1 className="f-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
        <MaskLines lines={[type === "research" ? "Peer-reviewed research" : type === "news" ? "Business analysis" : "Latest from BPJ"]} />
      </h1>
      <div className="mt-12 flex flex-col lg:flex-row lg:items-end gap-6 justify-between border-b border-[var(--ink)] pb-5">
        <div className="flex flex-wrap gap-2">
          {TYPES.map(([v, l]) => (
            <button key={l} onClick={() => setParams(v ? { type: v } : {})} data-testid={`filter-type-${v || "all"}`}
              className={`px-4 h-9 text-sm font-medium border transition-colors ${type === v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 lg:w-80">
          <Search size={17} className="text-[var(--muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, authors…" className="field" data-testid="articles-search-input" />
        </label>
      </div>
      <div className="flex gap-6 overflow-x-auto py-5 text-sm" data-lenis-prevent>
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`filter-cat-${c.toLowerCase()}`}
            className={`whitespace-nowrap pb-1 border-b-2 transition-colors ${cat === c ? "border-[var(--red)] text-[var(--ink)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`}>{c}</button>
        ))}
      </div>
      {!loading && list.length === 0 && <p className="py-24 text-center text-[var(--muted)]" data-testid="articles-empty">No articles match your filters.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 mt-10">
        {list.map((a, i) => <Reveal key={a.slug} delay={(i % 3) * 0.08}><ArticleCard a={a} /></Reveal>)}
      </div>
    </div>
  );
}
